import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, EmptyState, Field } from "@/components/common/States";
import { FormDialog, useAction } from "@/pages/ops/common";
import { Table, StatusBadge, money } from "./shared";

const KIND = { service: "Service (before work)", expense: "Additional expense", delivery: "Delivery" };

const LimitCard = () => {
  const { can } = useSession();
  const { data, reload } = useFetch("/garage-settings");
  const { run, busy } = useAction(reload);
  const [val, setVal] = useState(null);
  if (!data) return null;
  const cur = val ?? (data.expense_limit ?? "");
  return (
    <div className="g39-card p-4 mb-6 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="expense-limit-card">
      <Field label="Additional expense limit (₹)" hint="Services costing more than this need additional expense approval, even within the estimate. Leave blank for no limit." className="flex-1">
        <Input type="number" min="0" value={cur} disabled={!can("company.edit")} onChange={(e) => setVal(e.target.value)} data-testid="expense-limit-input" />
      </Field>
      {can("company.edit") && <Button disabled={busy} onClick={() => run("put", "/garage-settings", { expense_limit: cur === "" ? null : Number(cur) }, "Limit saved")} data-testid="expense-limit-save">Save limit</Button>}
    </div>
  );
};

export default function ServiceApprovals() {
  const [status, setStatus] = useState("pending");
  const { data, loading, error, reload } = useFetch("/service-approvals", { status });
  const { run } = useAction(reload);
  const [dec, setDec] = useState(null);
  const cols = [
    ["Requested", (a) => <span className="whitespace-nowrap">{fmtDateTime(a.requested_at)}</span>],
    ["Gate", (a) => KIND[a.kind]],
    ["Service", (a) => <Link to={`/service-records/${a.record_id}`} className="font-mono-g text-gold" data-testid={`approval-record-link-${a.id}`}>{a.record_number}</Link>],
    ["Vehicle", (a) => <span className="font-mono-g">{a.registration_number}</span>],
    ["Amount", (a) => money(a.amount), "text-right"],
    ["Requested by", (a) => a.requested_by_name],
    ["Status", (a) => <div><StatusBadge s={a.status} testId={`approval-list-status-${a.id}`} />{a.decided_by_name && <div className="text-[11px] text-muted-foreground mt-0.5">{a.decided_by_name} · {fmtDateTime(a.decided_at)}{a.comment ? ` — ${a.comment}` : ""}</div>}</div>],
    ["", (a) => (a.can_decide ? (
      <div className="flex gap-1.5 justify-end">
        <Button size="sm" onClick={() => setDec({ a, d: "approved" })} data-testid={`list-approve-${a.id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
        <Button size="sm" variant="outline" onClick={() => setDec({ a, d: "rejected" })} data-testid={`list-reject-${a.id}`}><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
      </div>
    ) : a.status === "pending" ? <span className="text-[11px] text-muted-foreground">Awaiting another approver</span> : null), "text-right"],
  ];
  return (
    <div data-testid="service-approvals-page">
      <PageHeader title="Approvals" subtitle="Service, extra expense and delivery approvals. You cannot approve your own request." />
      <LimitCard />
      <Tabs value={status} onValueChange={setStatus} className="mb-4"><TabsList>
        <TabsTrigger value="pending" data-testid="approvals-tab-pending">Pending{data && status === "pending" ? ` (${data.total})` : ""}</TabsTrigger>
        <TabsTrigger value="approved" data-testid="approvals-tab-approved">Approved</TabsTrigger>
        <TabsTrigger value="rejected" data-testid="approvals-tab-rejected">Rejected</TabsTrigger>
        <TabsTrigger value="all" data-testid="approvals-tab-all">All</TabsTrigger>
      </TabsList></Tabs>
      <div className="g39-card">
        {loading && <LoadingRows rows={4} />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.items.length && <EmptyState title={status === "pending" ? "Nothing waiting for approval" : "No approvals"} testId="approvals-empty" />}
        {data && data.items.length > 0 && <Table cols={cols} rows={data.items} testId="approvals-table" />}
      </div>
      <FormDialog open={!!dec} onOpenChange={() => setDec(null)} title={dec?.d === "approved" ? `Approve ${KIND[dec?.a.kind] || ""}` : "Reject request"} description={dec ? `${dec.a.record_number} · ${dec.a.registration_number} · ${money(dec.a.amount)}${dec.d === "rejected" ? " — a reason is required" : ""}` : ""} testId="list-decide-dialog"
        fields={[{ name: "comment", label: "Comment", textarea: true }]} onSubmit={(v) => run("post", `/service-approvals/${dec.a.id}/decide`, { decision: dec.d, comment: v.comment }, `Request ${dec.d}`)} />
    </div>
  );
}
